import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Gig } from '@/types/gig'
import { RootState } from '@/store'
import { useAppDispatch } from '@/store/hooks'
import { fetchCategories } from '@/store/slices/gigsSlice'
import {
    FiHeart,
    FiSearch,
    FiBookmark,
    FiRefreshCw
} from 'react-icons/fi'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import GigCard from '@/components/gigs/GigCard'
import SelectDropdown from '@/components/ui/SelectDropdown'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { gigApi } from '@/services/api/gigs'

// Helper to get category name whether it's string or object
const getCategoryName = (category: any, categoriesList?: any[]) => {
    if (!category) return 'Unknown'
    if (typeof category === 'string') {
        // If category is an ID string, try to find it in the categories list
        if (categoriesList) {
            const found = categoriesList.find(cat => cat._id === category)
            if (found) return found.slug
        }
        return category
    }
    return category.name || category.slug || 'Unknown'
}

// Helper to convert subcategory name to slug
const slugify = (text: string): string => {
    return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')
}

interface FavoriteGigsResponse {
    gigs: Gig[]
    totalCount: number
    currentPage: number
    totalPages: number
}

const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'amount', label: 'Highest Pay' },
    { value: 'title', label: 'Title A-Z' }
] as const

type SortOption = typeof SORT_OPTIONS[number]['value']

const FavoritesPage: React.FC = () => {
    const dispatch = useAppDispatch()
    const [favoriteGigs, setFavoriteGigs] = useState<Gig[]>([])
    const [allFavoriteGigs, setAllFavoriteGigs] = useState<Gig[]>([]) // Store all gigs for client-side filtering
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [categoryFilter, setCategoryFilter] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [sortBy, setSortBy] = useState<SortOption>('newest')
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalCount: 0
    })

    const { showSuccess, showError } = useSnackbarContext()
    const currentUser = useSelector((state: RootState) => state.auth.user)
    const categories = useSelector((state: RootState) => state.gigs.categories)

    // Fetch all favorite gigs from API (we'll do client-side filtering for better UX)
    const fetchFavoriteGigs = useCallback(async (showLoading: boolean = true) => {
        try {
            if (showLoading) {
                setLoading(true)
            }

            // Fetch all favorite gigs at once for better filtering experience
            const response = await gigApi.getFavoriteGigs({
                page: 1,
                limit: 100 // Get all favorites
            })

            if (response.success) {
                const data = response.data as FavoriteGigsResponse
                setAllFavoriteGigs(data.gigs)
                setPagination({
                    currentPage: 1,
                    totalPages: 1,
                    totalCount: data.totalCount
                })
            }
        } catch (error: any) {
            console.error('Failed to fetch favorite gigs:', error)
            const errorMessage = error.response?.data?.error?.message || 'Failed to load favorite gigs'
            showError(errorMessage)
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [showError])

    // Initial load - fetch categories and favorite gigs
    useEffect(() => {
        dispatch(fetchCategories())
        fetchFavoriteGigs()
    }, [dispatch, fetchFavoriteGigs])

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true)
        await fetchFavoriteGigs(false)
    }

    // Filter and sort gigs based on current filters
    const filteredAndSortedGigs = useMemo(() => {
        let filtered = allFavoriteGigs

        // Apply category filter (check both main category and subcategory)
        if (categoryFilter && categoryFilter !== 'all') {
            filtered = filtered.filter(gig => {
                const mainCat = getCategoryName(gig.category, categories)
                const isCategoryMatch = mainCat === categoryFilter
                // Normalize subcategory to slug for comparison
                const subcatSlug = gig.subcategory ? slugify(gig.subcategory) : ''
                const isSubcategoryMatch = subcatSlug === categoryFilter

                return isCategoryMatch || isSubcategoryMatch
            })
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = filtered.filter(gig =>
                gig.title.toLowerCase().includes(query) ||
                gig.description.toLowerCase().includes(query) ||
                getCategoryName(gig.category, categories).toLowerCase().includes(query) ||
                (gig.subcategory && gig.subcategory.toLowerCase().includes(query))
            )
        }

        // Apply sorting
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                case 'oldest':
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                case 'amount':
                    return b.payment.amount - a.payment.amount
                case 'title':
                    return a.title.localeCompare(b.title)
                default:
                    return 0
            }
        })

        return sorted
    }, [allFavoriteGigs, categoryFilter, searchQuery, sortBy, categories])

    // Update displayed gigs when filtering changes
    useEffect(() => {
        setFavoriteGigs(filteredAndSortedGigs)
    }, [filteredAndSortedGigs])

    // Get all categories + subcategories from Redux
    const availableCategories = useMemo(() => {
        if (!categories || categories.length === 0) return []
        const allCats = new Set<string>()
        categories.forEach(cat => {
            allCats.add(cat.slug)
            // API returns subcategories as strings (names), not objects
            if (cat.subcategories && Array.isArray(cat.subcategories) && cat.subcategories.length > 0) {
                cat.subcategories.forEach((subcat: any) => {
                    // Handle both string names and objects with slug property
                    const subcatValue = typeof subcat === 'string' ? slugify(subcat) : subcat.slug
                    allCats.add(subcatValue)
                })
            }
        })
        return Array.from(allCats).sort()
    }, [categories])

    // Get category counts (both main categories and subcategories)
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = { all: allFavoriteGigs.length }
        allFavoriteGigs.forEach(gig => {
            // Count main category
            const categorySlug = getCategoryName(gig.category, categories)
            counts[categorySlug] = (counts[categorySlug] || 0) + 1

            // Count subcategory if it exists
            if (gig.subcategory) {
                // Normalize subcategory to slug format for consistent counting
                const subcatSlug = slugify(gig.subcategory)
                counts[subcatSlug] = (counts[subcatSlug] || 0) + 1
            }
        })
        return counts
    }, [allFavoriteGigs, categories])

    // Build category dropdown options
    const categoryOptions = useMemo(() => {
        const opts = [{ value: 'all', label: 'All Categories' }]
        availableCategories.forEach((categorySlug) => {
            // Check if this is a main category
            const mainCat = categories?.find(cat => cat.slug === categorySlug)
            if (mainCat) {
                const displayName = `${mainCat.icon} ${mainCat.name}`
                opts.push({ value: categorySlug, label: displayName })
                return
            }

            // Otherwise, it's a subcategory - find parent category
            let subCatName: string = ''
            let parentIcon = ''
            categories?.forEach(cat => {
                // API returns subcategories as strings (names)
                const found = cat.subcategories?.find((sub: any) => {
                    const subSlug = typeof sub === 'string' ? slugify(sub) : sub.slug
                    return subSlug === categorySlug
                })
                if (found) {
                    subCatName = typeof found === 'string' ? found : found.name
                    parentIcon = cat.icon || ''
                }
            })

            if (subCatName) {
                const displayName = `  ${parentIcon} ${subCatName}`
                opts.push({ value: categorySlug, label: displayName })
            }
        })
        return opts
    }, [availableCategories, categories])

    // Handle remove from favorites
    const handleRemoveFromFavorites = async (gigId: string) => {
        try {
            // Store original gigs for potential rollback
            const originalGigs = allFavoriteGigs

            // Optimistically remove from favorites UI
            setAllFavoriteGigs(prev => prev.filter(gig => gig._id !== gigId))

            const response = await gigApi.toggleFavorite(gigId)

            if (response.data && response.data.isFavorited === false) {
                showSuccess('Removed from favorites')
                // Update pagination count
                setPagination(prev => ({
                    ...prev,
                    totalCount: Math.max(0, prev.totalCount - 1)
                }))
            } else {
                // Revert if the response doesn't indicate successful removal
                setAllFavoriteGigs(originalGigs)
            }
        } catch (error: any) {
            // Revert optimistic update on error
            const originalGigs = allFavoriteGigs
            setAllFavoriteGigs(originalGigs)
            console.error('Failed to remove from favorites:', error)
            const errorMessage = error.response?.data?.error?.message || 'Failed to remove from favorites'
            showError(errorMessage)
        }
    }

    // Handle favorite toggle (for GigCard component)
    const handleFavoriteToggle = async (gigId: string) => {
        await handleRemoveFromFavorites(gigId)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>My Favorites - SideGO</title>
                <meta name="description" content="Manage your favorite gigs and saved opportunities" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <FiHeart className="w-8 h-8 text-red-500" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            My Favorites
                        </h1>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Manage your favorite gigs and never lose track of opportunities you're interested in
                    </p>
                </div>

                {allFavoriteGigs.length === 0 ? (
                    // No favorites at all - empty state
                    <div className="text-center py-16">
                        <FiBookmark className="w-24 h-24 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            No Favorites Yet
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                            Start exploring gigs and save the ones you're interested in.
                            Click the heart icon on any gig to add it to your favorites.
                        </p>
                        <Link
                            to="/gigs"
                            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <FiSearch className="w-5 h-5 mr-2" />
                            Browse Gigs
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Search, Filters, Sort and Refresh */}
                        <div className="mb-6">
                            {/* Search Bar */}
                            <div className="mb-4">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search favorite gigs..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Filters and Controls */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                {/* Category Filter */}
                                <div className="flex-1">
                                    <SelectDropdown
                                        options={categoryOptions}
                                        value={categoryFilter}
                                        onChange={(value) => setCategoryFilter(value)}
                                        label="Categories"
                                    />
                                </div>

                                {/* Sort By */}
                                <div className="flex-1 sm:flex-initial sm:w-48">
                                    <SelectDropdown
                                        options={SORT_OPTIONS}
                                        value={sortBy}
                                        onChange={(value) => setSortBy(value as SortOption)}
                                        label="Sort By"
                                    />
                                </div>

                                {/* Refresh Button */}
                                <button
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="inline-flex items-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                    {refreshing ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>
                        </div>

                        {/* Results count */}
                        {favoriteGigs.length > 0 && (
                            <div className="mb-6 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                                <span>
                                    Showing {favoriteGigs.length} of {pagination.totalCount} favorite gigs
                                </span>
                            </div>
                        )}

                        {/* Gigs Grid */}
                        {favoriteGigs.length === 0 ? (
                            <div className="text-center py-16">
                                <FiSearch className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    No gigs match your search
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Try adjusting your search terms or filters
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                {favoriteGigs.map((gig) => (
                                    <GigCard
                                        key={gig._id}
                                        gig={gig}
                                        onFavorite={handleFavoriteToggle}
                                        isFavorited={true} // All gigs in favorites page are favorited
                                        showEmployer={true}
                                        currentUser={currentUser}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    )
}

export default FavoritesPage